import type { PhotoAttachment } from './schema';

export const MAX_SOURCE_PHOTO_BYTES = 12_000_000;
export const MAX_STORED_PHOTO_BYTES = 2_500_000;
export const MAX_PHOTOS_PER_MEMORY = 12;
export const MAX_PHOTOS_PER_TRIP = 48;

function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/webp', quality: number) {
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('사진을 변환하지 못했어요.')), type, quality));
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('사진을 읽지 못했어요.'));
    reader.readAsDataURL(blob);
  });
}

export async function preparePhoto(file: File, now = new Date().toISOString()): Promise<PhotoAttachment> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('JPG, PNG, WebP 사진만 올릴 수 있어요.');
  if (file.size > MAX_SOURCE_PHOTO_BYTES) throw new Error('원본 사진은 한 장에 12MB까지 올릴 수 있어요.');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('사진 파일을 해석하지 못했어요. 다른 JPG, PNG 또는 WebP 사진을 골라주세요.');
  }
  const scale = Math.min(1, 1_600 / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('사진을 변환할 수 없는 브라우저예요.');
  context.fillStyle = '#f6f4ef';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  let output = await canvasToBlob(canvas, 'image/jpeg', .82);
  if (output.size > MAX_STORED_PHOTO_BYTES) output = await canvasToBlob(canvas, 'image/jpeg', .65);
  if (output.size > MAX_STORED_PHOTO_BYTES) throw new Error('사진을 줄인 뒤에도 2.5MB를 넘어요. 더 작은 사진을 골라주세요.');
  return {
    id: `photo-${crypto.randomUUID()}`,
    name: file.name.replace(/\.[^.]+$/, '').slice(0, 110) || '여행 사진',
    mimeType: 'image/jpeg',
    size: output.size,
    width,
    height,
    dataUrl: await blobToDataUrl(output),
    locationIncluded: false,
    createdAt: now,
  };
}

export function tripPhotoCount(memories: readonly { photos: readonly unknown[] }[]) {
  return memories.reduce((sum, memory) => sum + memory.photos.length, 0);
}
